import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Boot-Zeilen VOR Cookie-Consent
const BOOT_PRE = [
  { type: "prompt", text: "$ boot denisbitter.de" },
  { type: "response", text: "> loading modules..." },
];

// Boot-Zeilen NACH Cookie-Consent
const BOOT_POST = [
  { type: "response", text: "> initializing ui..." },
  { type: "response", text: "> moin aus hamburg." },
  { type: "status", text: "> ready." },
];

const TYPING_SPEED = 30;
const PROMPT_PAUSE = 300;
const LINE_PAUSE = 150;

function useTypewriter(lines, startDelay = 400) {
  const [visibleLines, setVisibleLines] = useState([]);
  const [lineIdx, setLineIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (lineIdx >= lines.length) {
      setDone(true);
      return;
    }

    const line = lines[lineIdx];

    if (line.type === "prompt") {
      const timer = setTimeout(() => {
        setVisibleLines((prev) => [...prev, { ...line, displayText: line.text }]);
        setLineIdx((i) => i + 1);
        setCharIdx(0);
      }, lineIdx === 0 ? startDelay : PROMPT_PAUSE);
      return () => clearTimeout(timer);
    }

    if (charIdx === 0) {
      const timer = setTimeout(() => {
        setVisibleLines((prev) => [...prev, { ...line, displayText: "" }]);
        setCharIdx(1);
      }, LINE_PAUSE);
      return () => clearTimeout(timer);
    }

    if (charIdx <= line.text.length) {
      const timer = setTimeout(() => {
        setVisibleLines((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { ...line, displayText: line.text.slice(0, charIdx) };
          return updated;
        });
        setCharIdx((c) => c + 1);
      }, TYPING_SPEED);
      return () => clearTimeout(timer);
    }

    const timer = setTimeout(() => {
      setLineIdx((i) => i + 1);
      setCharIdx(0);
    }, LINE_PAUSE);
    return () => clearTimeout(timer);
  }, [lines, lineIdx, charIdx, startDelay]);

  const isTyping = lineIdx < lines.length;

  return { visibleLines, isTyping, done };
}

function TerminalLine({ line, showCursor }) {
  if (line.type === "prompt") {
    return (
      <p className="text-sm text-[#EFEBDC]/50 mt-2 first:mt-0">
        <span className="text-[#AC8E66]">$</span>
        {line.displayText.slice(1)}
      </p>
    );
  }
  if (line.type === "status") {
    return (
      <p className="text-sm text-green-400 mt-1">
        <span className="text-green-400/60">&gt;</span>
        {line.displayText.slice(1)}
        {showCursor && <span className="inline-block w-[2px] h-[1em] bg-[#AC8E66] ml-[1px] align-middle animate-cursor-blink" />}
      </p>
    );
  }
  return (
    <p className="text-sm text-[#EFEBDC]/80 mt-1">
      <span className="text-[#AC8E66]/80">&gt;</span>
      {line.displayText.slice(1)}
      {showCursor && <span className="inline-block w-[2px] h-[1em] bg-[#AC8E66] ml-[1px] align-middle animate-cursor-blink" />}
    </p>
  );
}

// Cookie-Consent inline im Terminal
function BootCookieConsent({ onSave }) {
  const [consent, setConsent] = useState(() => {
    const stored = localStorage.getItem("cookieConsentV2");
    return stored ? JSON.parse(stored) : { essential: true, statistics: false, media: false };
  });

  const handleToggle = (key) => {
    setConsent((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    localStorage.setItem("cookieConsentV2", JSON.stringify(consent));
    onSave();
  };

  return (
    <div className="mt-3">
      <p className="text-sm text-orange-400 mb-3">
        <span className="text-orange-400/60">&gt;</span> cookie consent required.
      </p>

      <div className="ml-4 space-y-2">
        {/* Essentiell */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#EFEBDC]/60">essentiell</span>
          <span className="text-xs text-green-400">aktiv</span>
        </div>

        {/* Statistik */}
        <div className="flex items-center justify-between">
          <span id="preloader-consent-statistics-label" className="text-xs text-[#EFEBDC]/60">statistik (matomo)</span>
          <label htmlFor="preloader-consent-statistics" className="inline-flex items-center cursor-pointer">
            <input id="preloader-consent-statistics" type="checkbox" checked={consent.statistics} onChange={() => handleToggle("statistics")} className="sr-only" aria-labelledby="preloader-consent-statistics-label" />
            <div className={`w-8 h-4 rounded-full relative transition ${consent.statistics ? "bg-green-500/30" : "bg-[#EFEBDC]/10"}`}>
              <div className={`w-3 h-3 rounded-full absolute top-0.5 left-0.5 transition-transform ${consent.statistics ? "translate-x-4 bg-green-400" : "bg-[#EFEBDC]/40"}`} />
            </div>
          </label>
        </div>

        {/* Medien */}
        <div className="flex items-center justify-between">
          <span id="preloader-consent-media-label" className="text-xs text-[#EFEBDC]/60">externe medien</span>
          <label htmlFor="preloader-consent-media" className="inline-flex items-center cursor-pointer">
            <input id="preloader-consent-media" type="checkbox" checked={consent.media} onChange={() => handleToggle("media")} className="sr-only" aria-labelledby="preloader-consent-media-label" />
            <div className={`w-8 h-4 rounded-full relative transition ${consent.media ? "bg-green-500/30" : "bg-[#EFEBDC]/10"}`}>
              <div className={`w-3 h-3 rounded-full absolute top-0.5 left-0.5 transition-transform ${consent.media ? "translate-x-4 bg-green-400" : "bg-[#EFEBDC]/40"}`} />
            </div>
          </label>
        </div>
      </div>

      <button
        onClick={handleSave}
        className="mt-3 w-full py-2 text-xs text-[#EFEBDC]/70 bg-[#AC8E66]/15 border border-[#AC8E66]/20 rounded hover:bg-[#AC8E66]/25 transition-colors cursor-pointer"
      >
        <span className="text-[#AC8E66]">$</span> save --cookies
      </button>
    </div>
  );
}

const Preloader = ({ children, isReady }) => {
  const [phase, setPhase] = useState("boot-pre"); // boot-pre → cookie → boot-post → done
  const [needsCookieConsent] = useState(() => !localStorage.getItem("cookieConsentV2"));

  // Phase 1: Boot-Pre
  const pre = useTypewriter(BOOT_PRE, 400);

  // Wenn Boot-Pre fertig → Cookie oder weiter
  useEffect(() => {
    if (phase === "boot-pre" && pre.done) {
      if (needsCookieConsent) {
        setPhase("cookie");
      } else {
        setPhase("boot-post");
      }
    }
  }, [phase, pre.done, needsCookieConsent]);

  // Phase 2: Cookie-Consent gespeichert
  const handleCookieSave = useCallback(() => {
    setPhase("boot-post");
  }, []);

  // Phase 3: Boot-Post
  const post = useTypewriter(phase === "boot-post" ? BOOT_POST : [], 200);

  useEffect(() => {
    if (phase === "boot-post" && post.done) {
      setPhase("done");
    }
  }, [phase, post.done]);

  // Alle sichtbaren Zeilen zusammen
  const allLines = [
    ...pre.visibleLines,
    ...(phase === "cookie" && pre.done ? [{ type: "prompt", displayText: "$ cookie --config" }] : []),
    ...post.visibleLines,
  ];

  const isTyping = phase === "boot-pre" ? pre.isTyping : phase === "boot-post" ? post.isTyping : false;

  return (
    <>
      {children}

      <AnimatePresence>
        {!isReady && (
          <motion.div
            key="preloader"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, filter: "blur(4px)" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="fixed inset-0 z-50 flex items-center justify-center font-mono"
            style={{ background: "linear-gradient(180deg, #121212, #151515)" }}
          >
            <div className="w-full max-w-md mx-4 rounded-lg border border-[#AC8E66]/20 overflow-hidden shadow-2xl shadow-black/40">

              {/* Title Bar */}
              <div className="flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
                <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                <span className="ml-2 text-xs text-[#AC8E66] select-none">boot</span>
              </div>

              {/* Terminal Content */}
              <div className="px-4 py-4 bg-[#151515] min-h-[140px]">
                {allLines.map((line, i) => (
                  <TerminalLine key={i} line={line} showCursor={isTyping && i === allLines.length - 1} />
                ))}

                {/* Cookie-Consent inline */}
                {phase === "cookie" && <BootCookieConsent onSave={handleCookieSave} />}

                {/* Consent gespeichert Zeile */}
                {phase === "boot-post" && post.visibleLines.length === 0 && (
                  <p className="text-sm text-green-400 mt-1">
                    <span className="text-green-400/60">&gt;</span> consent saved.
                  </p>
                )}

                {/* Blinkender Cursor am Ende */}
                {phase === "done" && (
                  <p className="text-sm text-[#EFEBDC]/50 mt-2">
                    <span className="text-[#AC8E66]">$</span>
                    <span className="inline-block w-[2px] h-[1em] bg-[#AC8E66] ml-1 align-middle animate-cursor-blink" />
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Preloader;
